import React, { useRef } from "react";

const CustomAccordion = ({ children, value, onValueChange, className }) => {
  const itemRefs = useRef({});

  const handleToggle = (newValue, manualScroll = false) => {
    const prevActiveItem = value;
    const newActiveItem = value === newValue ? "" : newValue;

    if (onValueChange) onValueChange(newActiveItem);

    if (
      manualScroll ||
      !newActiveItem ||
      !prevActiveItem ||
      !itemRefs.current[prevActiveItem] ||
      !itemRefs.current[newActiveItem]
    ) {
      return;
    }

    const prevItemElement = itemRefs.current[prevActiveItem];
    const newItemElement = itemRefs.current[newActiveItem];
    const prevContentElement = prevItemElement.querySelector(".accordion-content");

    const prevTop = prevItemElement.getBoundingClientRect().top;
    const newTop = newItemElement.getBoundingClientRect().top;

    if (newTop > prevTop && prevContentElement) {
      const offsetAdjustment = prevContentElement.offsetHeight;
      window.scrollBy(0, -offsetAdjustment);
    }
  };

  return (
    <div className={`custom-accordion ${className || ""}`}>
      {React.Children.map(children, (child, index) => {
        if (!child || !child.props || typeof child.props.value === "undefined") {
          return null;
        }

        return React.cloneElement(child, {
          isOpen: child.props.value === value,
          onToggle: (manualScroll) => handleToggle(child.props.value, manualScroll),
          ref: (el) => (itemRefs.current[child.props.value] = el),
          key: index,
        });
      })}
    </div>
  );
};

CustomAccordion.displayName = "CustomAccordion";

const CustomAccordionItem = React.forwardRef(
  ({ value, children, className, isOpen, onToggle }, ref) => {
    return (
      <div
        ref={ref}
        className={`accordion-item ${className || ""} ${isOpen ? "open" : ""}`}
      >
        {React.Children.map(children, (child) => {
          if (child.type === CustomAccordionTrigger) {
            return React.cloneElement(child, { onClick: onToggle, isOpen });
          }
          if (child.type === CustomAccordionContent) {
            return React.cloneElement(child, { isOpen });
          }
          return child;
        })}
      </div>
    );
  }
);
CustomAccordionItem.displayName = "CustomAccordionItem";

const CustomAccordionTrigger = ({ children, onClick, className, isOpen }) => (
  <div
    onClick={() => onClick(false)}
    className={`accordion-trigger ${className || ""}`}
  >
    <div className="flex items-center justify-between w-full">
      {children}
      <span className="accordion-icon text-blue3 text-[20px] lg:text-[16px]">
        <div className={isOpen ? "rotate-180" : "rotate-0"}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24px"
            height="12px"
            fill="none"
            viewBox="0 0 15 9"
          >
            <path
              stroke="#0C2340"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M1 1.023l6.5 6.96 6.5-6.96"
            />
          </svg>
        </div>
      </span>
    </div>
  </div>
);
CustomAccordionTrigger.displayName = "CustomAccordionTrigger";

const CustomAccordionContent = ({ children, isOpen, className }) => (
  <div
    className={`accordion-content ${className || ""} ${
      isOpen ? "open" : "closed"
    }`}
    style={{ display: isOpen ? "block" : "none" }}
  >
    {children}
  </div>
);
CustomAccordionContent.displayName = "CustomAccordionContent";

export {
  CustomAccordion,
  CustomAccordionItem,
  CustomAccordionTrigger,
  CustomAccordionContent,
};
